import React, { useEffect, useState } from "react";
import { adminServices } from "../services/AdminServices";
import DataTable from "react-data-table-component";
import { ReactComponent as TransactionIcon } from '../icons/transactions.svg';
import LoadingModal from "../components/LoadingModal";
import SearchBar from "../components/SearchBar";
import Button from "../components/Button";
import { formatCurrency } from '../tools/tool';
import InputText1 from "../components/InputText1";
import withAuthorization from "../components/withAuthorization";
import { logExport } from "../services/ExportLogService";
const FundTransferReport = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [columns, setColumns] = useState([]);
  const [allData, setAllData] = useState([]);
  const [transactionType, setTransactionType] = useState('merchant');
  useEffect(() => {
    setLoading(true);
    getFundTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionType]);
  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, searchQuery, dateFrom, dateTo]);
  const filterData = () => {
    let filtered = allData;
    if (dateFrom || dateTo) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.timestamp);
        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
        if (fromDate && itemDate < fromDate) return false;
        if (toDate && itemDate > toDate) return false;
        return true;
      });
    }
    if (searchQuery) {
      filtered = filtered.filter(item => Object.keys(item).some(key => item[key] && item[key].toString().toLowerCase().includes(searchQuery.toLowerCase())));
    }
    setFilteredData(filtered);
    let gT = filtered.reduce((total, item) => total + (item.amount || 0), 0);
    setGrandTotal(gT);
  };
  const getFundTransfers = async () => {
    setLoading(true);
    try {
      let params = {};
      let procedureName;
      if (dateFrom && dateTo) {
        procedureName = transactionType === 'merchant' ? 'fund_transfer_merchant_report_range' : 'fund_transfer_pdl_report_range';
        params = {
          dateFrom: dateFrom,
          dateTo: dateTo
        };
      } else {
        procedureName = transactionType === 'merchant' ? 'fund_transfer_merchant_report' : 'fund_transfer_pdl_report';
        params = {};
      }
      const response = await adminServices.storeProcedure2(procedureName, params);
      if (response.data && response.data.length > 0) {
        const processedData = response.data.map(item => ({
          ...item,
          timestamp: item.timestamp || item.transaction_date || item.reg_date || item.txDate,
          merchantName: item.merchantName || item.merchant_name || extractMerchantName(item.remarks) || '',
          pdlName: item.pdlName || item.pdl_name || (item.first_name && item.last_name ? `${item.first_name} ${item.last_name}` : '') || '',
          pdlNumber: item.pdlNumber || item.pdl_number || extractPDLNumber(item.remarks) || '',
          refNo: item.refNo || item.ref_no || item.reference_no || '',
          amount: item.amount || 0,
          remarks: item.remarks || '',
          interestRate: item.interest_rate || item.interestRate || 0,
          interestAmount: item.interest_amount || item.interestAmount || 0,
          senderName: item.sender_name || item.senderName || item.sender || ''
        }));
        setAllData(processedData);
        buildColumns();
      } else {
        setAllData([]);
        buildColumns();
      }
      setLoading(false);
    } catch (e) {
      console.error('Error fetching fund transfers:', e);
      if (e.status === 401) {
        window.location.href = '/';
      } else {
        setAllData([]);
        buildColumns();
      }
      setLoading(false);
    }
  };
  const extractMerchantName = remarks => {
    if (!remarks) return '';
    const match = remarks.match(/Merchant:\s*(.+?)(?:;|$)/i) || remarks.match(/From Merchant:\s*(.+?)(?:;|$)/i) || remarks.match(/To Merchant:\s*(.+?)(?:;|$)/i);
    return match ? match[1].trim() : '';
  };
  const extractPDLNumber = remarks => {
    if (!remarks) return '';
    const match = remarks.match(/Fund Transfer (?:To|From)\s+([A-Z0-9-]+)/i);
    return match ? match[1].trim() : '';
  };
  const buildColumns = () => {
    const cols = [{
      name: 'Transaction Date',
      selector: data => new Date(data.timestamp).toLocaleString(),
      sortable: true,
      width: '180px',
      defaultSortAsc: false
    }, {
      name: 'Reference No',
      selector: data => data.refNo,
      sortable: true,
      width: '150px'
    }];
    if (transactionType === 'merchant') {
      cols.push({
        name: 'PDL Number',
        selector: data => data.pdlNumber || 'N/A',
        sortable: true,
        width: '150px'
      });
    }
    cols.push({
      name: 'Sender Name',
      selector: data => data.senderName || 'N/A',
      sortable: true,
      width: '180px'
    }, {
      name: 'Remarks',
      selector: data => data.remarks,
      sortable: true,
      width: '250px'
    }, {
      name: 'Interest Rate',
      selector: data => `${(data.interestRate || 0).toFixed(2)}%`,
      sortable: true,
      right: true,
      width: '130px',
      headerStyle: {
        textAlign: 'right'
      }
    }, {
      name: 'Interest Amount',
      selector: data => formatCurrency(data.interestAmount || 0),
      sortable: true,
      right: true,
      width: '150px',
      headerStyle: {
        textAlign: 'right'
      }
    }, {
      name: 'Amount',
      selector: data => formatCurrency(data.amount),
      sortable: true,
      right: true,
      width: '150px',
      headerStyle: {
        textAlign: 'right'
      }
    });
    setColumns(cols);
  };
  const exportToCSV = async data => {
    if (!data || data.length === 0) return;

    // Log the export
    await logExport('Fund Transfer Report');
    const headers = ['Transaction Date', 'Reference No'];

    // Only add PDL Number header for Merchant Transactions
    if (transactionType === 'merchant') {
      headers.push('PDL Number');
    }
    headers.push('Sender Name', 'Remarks', 'Interest Rate', 'Interest Amount', 'Amount');
    const rows = data.map(row => {
      const baseRow = [new Date(row.timestamp).toLocaleString(), row.refNo];

      // Only add PDL Number for Merchant Transactions
      if (transactionType === 'merchant') {
        baseRow.push(row.pdlNumber || 'N/A');
      }
      baseRow.push(row.senderName || 'N/A', row.remarks, `${(row.interestRate || 0).toFixed(2)}%`, row.interestAmount || 0, row.amount);
      return baseRow;
    });
    const csvContent = [headers, ...rows].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `FundTransferReport_${new Date().toISOString().split('T')[0]}.csv`);
    link.click();
  };
  const handleDateFilter = async () => {
    setLoading(true);
    setAllData([]);
    setFilteredData([]);
    await getFundTransfers();
  };
  return <div>
      <LoadingModal show={loading} message={"Loading Fund Transfer Report"} />
      <div style={{
      fontSize: '1.5rem',
      fontWeight: 'bold',
      marginBottom: 10,
      display: 'flex',
      justifyContent: 'start',
      alignItems: 'center'
    }}>
          <TransactionIcon style={{
        width: "50px",
        height: "50px",
        marginRight: "10px",
        background: 'transparent'
      }} />
          Fund Transfer Report ({formatCurrency(grandTotal)})
      </div>

      <div className="flexRow" style={{
      width: '50%',
      marginBottom: '15px'
    }}>
          <InputText1 title={"Date From"} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />

         <InputText1 title={"Date To"} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />

          <div style={{
        marginTop: 18
      }}>
            <Button variant={"primary"} onClick={handleDateFilter}>Filter</Button>
          </div>
      </div>

      <div className="flexRow">
        <div className="flexRow filterDiv">
          <SearchBar placeholder="Search Fund Transfer Report" onChange={e => {
          setSearchQuery(e.target.value.toLowerCase());
        }} />
          <div style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: 10
        }}>
            <span style={{
            fontWeight: 'bold',
            marginRight: 8
          }}>Type:</span>
            <select value={transactionType} onChange={e => {
            setTransactionType(e.target.value);
            setAllData([]);
            setFilteredData([]);
          }} style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
            minWidth: '200px'
          }}>
              <option value="merchant">Merchant Transactions</option>
              <option value="pdl">PDL Transactions</option>
            </select>
          </div>
        </div>
        <div className="spacer"></div>
        <Button variant={"success"} size={'xs'} onClick={() => exportToCSV(filteredData)}> 
          Export to CSV 
        </Button>
      </div>
      
      <br />
      <DataTable columns={columns} data={filteredData} pagination={false} persistTableHead persistTableBody highlightOnHover style={{
      zIndex: 0
    }} defaultSortFieldId="timestamp" />
    </div>;
};
export default withAuthorization(FundTransferReport, 34);